import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { BarList, EmptyState, type BarListItem } from '@vnetwork/ui/data';
import { useDomainTopPaths } from '@/api/manticore';
import { formatCount } from '../../../_shared/analytics-format';

/** Top URLs/paths card — LIVE vendor pull for a single domain. The endpoint is
 *  per-domain, so a card-local picker chooses which of the service's domains to
 *  inspect (defaults to the first). One request per view, only while enabled.
 *
 *  URL-level data exists only on vEdge (Raven). Eagle/Conversant returns its BI
 *  bundle (no path concept) and EdgeNext/Hawk reports unsupported — the client
 *  merge drops both, so a domain without a vEdge binding shows "No data". */
export function TopPathsCard({
  serviceId,
  domainOptions,
  period,
  from,
  to,
}: {
  serviceId?: string;
  domainOptions: { value: string; label: string }[];
  period: string;
  from?: string;
  to?: string;
}) {
  const [domainId, setDomainId] = useState<string>('');

  // Default to the first domain; reset when the service (and thus its domain
  // set) changes so we never query a stale domain id.
  useEffect(() => {
    setDomainId(domainOptions[0]?.value ?? '');
  }, [serviceId, domainOptions]);

  const q = useDomainTopPaths(domainId, { period, from, to, limit: 15, enabled: !!domainId });

  const items = useMemo<BarListItem[]>(
    () =>
      (q.data ?? []).map((r) => ({
        label: r.path,
        value: r.requests,
        display: formatCount(r.requests),
      })),
    [q.data],
  );

  return (
    <Card flat>
      <CardHeader
        actions={
          domainOptions.length > 0 ? (
            <Select value={domainId} onValueChange={setDomainId}>
              <SelectTrigger disabled={domainOptions.length === 0}>
                <SelectValue placeholder="Select domain" />
              </SelectTrigger>
              <SelectContent>
                {domainOptions.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : undefined
        }
      >
        <CardTitle>Top URLs</CardTitle>
        <CardDescription>Most-requested paths for the selected domain over the window.</CardDescription>
      </CardHeader>
      <CardBody>
        {q.isLoading ? (
          <Skeleton style={{ height: 160 }} />
        ) : items.length === 0 ? (
          <EmptyState size="sm" title="No data" description="No URL-level data for this domain and period." />
        ) : (
          <BarList items={items} />
        )}
      </CardBody>
    </Card>
  );
}
